/**
 * /about — Founder story page (D3.3 from the site audit).
 * Public, static, no auth required. Linked from the landing-page footer.
 */
import { Link } from "react-router-dom";
import { ArrowLeft, Linkedin, ArrowRight } from "lucide-react";
import { usePageSeo } from "../lib/seo";

export default function About() {
  usePageSeo({
    title: "About iKEY — Founders Felecia Stivers & Ethan Schaffino",
    description: "Meet the iKEY founders and read why they built the only workspace a solo residential real-estate agent needs — from lead to closing table.",
    canonical: "https://ikey-app.com/about",
  });
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="about-page">
      {/* Header — matches Landing.jsx */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200/60">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/ikey-logo.png" alt="iKEY" className="h-9 w-9 rounded-lg ring-1 ring-slate-200" />
            <span className="font-heading text-lg font-bold tracking-tight">iKEY</span>
          </Link>
          <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5" data-testid="about-back">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "var(--primary-hex)" }}>
          About iKEY
        </div>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-[1.05]" data-testid="about-title">
          Built for agents. Obsessed with their workflow.
        </h1>

        <div className="mt-10 rounded-2xl overflow-hidden ring-1 ring-slate-200 shadow-sm">
          <img
            src="/founders.png"
            alt="Felecia Stivers and Ethan Schaffino — founders of iKEY"
            className="w-full h-auto object-cover"
            loading="lazy"
            data-testid="about-founders-photo"
          />
        </div>
        <div className="mt-3 text-xs text-slate-500 text-center">
          Felecia Stivers &amp; Ethan Schaffino, iKEY co-founders.
        </div>

        <section className="mt-12">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Why we built iKEY</h2>
          <p className="mt-4 text-slate-700 leading-relaxed">
            We aren't real estate agents — we're software people who kept
            watching the agents in our lives run their entire business out of six
            different apps. A CRM they barely used. DocuSign in one tab. MLS
            clippings in another. A scattered client SMS thread. An e-signature
            vendor billing more per month than their transaction platform. And a
            daily-planner Post-it doing the actual work. Every hand-off leaked
            information. Every logged-out session cost a friend a lead. So we
            built the tool they wished existed: one workspace that scores every
            lead, runs every deal from a branded client portal, and signs the
            contract without leaving the page. iKEY is that tool — the only key
            an agent needs, from lead to sold.
          </p>
        </section>

        <div className="mt-10 flex items-center gap-4 flex-wrap">
          <a
            href="https://www.linkedin.com/in/feleciastivers/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white bg-[#0A66C2] hover:bg-[#004182]"
            data-testid="about-linkedin-felecia"
          >
            <Linkedin className="h-4 w-4" /> Felecia on LinkedIn
          </a>
          <a
            href="https://www.linkedin.com/in/ethan-schaffino-82aa46427/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white bg-[#0A66C2] hover:bg-[#004182]"
            data-testid="about-linkedin-ethan"
          >
            <Linkedin className="h-4 w-4" /> Ethan on LinkedIn
          </a>
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800"
            data-testid="about-cta"
          >
            Try iKEY free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </main>

      <footer className="border-t border-slate-200 py-10">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center text-sm text-slate-500">
          © {new Date().getFullYear()} iKEY, Inc. All rights reserved. ·{" "}
          <Link to="/terms" className="hover:text-slate-900">Terms</Link> ·{" "}
          <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
        </div>
      </footer>
    </div>
  );
}
